import { useState } from "react";
import { ArrowLeft, Activity, Clock, Zap, Flame } from "lucide-react";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Input } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/input";
import { Label } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/label";
import { Slider } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/slider";
import { Textarea } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/textarea";
import { useToast } from "@/projects/Admin_Bite_Balance/bite-balance/src/hooks/use-toast";
import { useNavigate } from "react-router-dom";

const LogActivity = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activityType, setActivityType] = useState("");
  const [duration, setDuration] = useState("");
  const [intensity, setIntensity] = useState([5]);
  const [notes, setNotes] = useState("");

  const activityTypes = [
    {
      name: "Walking",
      icon: Activity,
      color: "bg-chart-1",
      border: "border-chart-1",
    },
    {
      name: "Running",
      icon: Zap,
      color: "bg-chart-2",
      border: "border-chart-2",
    },
    {
      name: "Cycling",
      icon: Activity,
      color: "bg-chart-3",
      border: "border-chart-3",
    },
    {
      name: "Swimming",
      icon: Activity,
      color: "bg-chart-4",
      border: "border-chart-4",
    },
    { name: "Gym", icon: Flame, color: "bg-accent", border: "border-accent" },
    { name: "Yoga", icon: Activity, color: "bg-info", border: "border-info" },
  ];

  const intensityLabels = [
    "Very Light",
    "Light",
    "Moderate",
    "Hard",
    "Very Hard",
  ];

  const handleSubmit = () => {
    if (!activityType) {
      toast({
        title: "Please select an activity type",
        variant: "destructive",
      });
      return;
    }

    if (!duration) {
      toast({
        title: "Please enter duration",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Activity logged successfully!",
      description: `${activityType} for ${duration} minutes`,
    });

    setTimeout(() => navigate("/home"), 1500);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center gap-3 pb-1 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/home")}
            className="h-9 w-9 border-2 border-border"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-foreground">Log Activity</h1>
        </div>

        {/* Activity Type Selection */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <h3 className="mb-4 text-base font-bold text-foreground">
            Activity Type
          </h3>
          <div className="grid grid-cols-3 gap-3">
            {activityTypes.map(activity => {
              const Icon = activity.icon;
              return (
                <button
                  key={activity.name}
                  onClick={() => setActivityType(activity.name)}
                  className={`rounded-xl border-2 p-4 transition-all ${
                    activityType === activity.name
                      ? `${activity.border} ${activity.color}/20 shadow-md`
                      : "border-border bg-muted/10 hover:border-primary/40"
                  }`}
                >
                  <div
                    className={`h-10 w-10 rounded-lg ${activity.color}/20 mx-auto mb-2 flex items-center justify-center border-2 ${activity.border}`}
                  >
                    <Icon
                      className={`h-5 w-5 ${activity.color.replace("bg-", "text-")}`}
                    />
                  </div>
                  <p className="text-xs font-bold text-foreground">
                    {activity.name}
                  </p>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Duration Input */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="space-y-3">
            <Label
              htmlFor="duration"
              className="flex items-center gap-2 text-base font-bold text-foreground"
            >
              <Clock className="h-4 w-4" />
              Duration (minutes)
            </Label>
            <Input
              id="duration"
              type="number"
              placeholder="e.g., 30"
              value={duration}
              onChange={e => setDuration(e.target.value)}
              className="h-12 border-2 border-border text-base font-semibold"
            />
          </div>
        </Card>

        {/* Intensity Slider */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2 text-base font-bold text-foreground">
                <Zap className="h-4 w-4" />
                Intensity Level
              </Label>
              <span className="text-lg font-bold text-primary">
                {intensityLabels[Math.floor((intensity[0] / 10) * 4)]}
              </span>
            </div>
            <Slider
              value={intensity}
              onValueChange={setIntensity}
              max={10}
              min={1}
              step={1}
              className="py-4"
            />
            <div className="flex justify-between text-xs font-semibold text-muted-foreground">
              <span>Light</span>
              <span>Very Hard</span>
            </div>
          </div>
        </Card>

        {/* Estimated Calories */}
        <Card className="rounded-xl border-2 border-primary/30 bg-primary/5 p-5 shadow-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border-2 border-accent bg-accent/20">
                <Flame className="h-6 w-6 text-accent" />
              </div>
              <div>
                <p className="text-sm font-bold text-muted-foreground">
                  Estimated Calories
                </p>
                <p className="text-2xl font-bold text-foreground">
                  {duration && activityType
                    ? Math.round(Number(duration) * intensity[0] * 0.8)
                    : "---"}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Notes */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <div className="space-y-3">
            <Label
              htmlFor="notes"
              className="text-base font-bold text-foreground"
            >
              Notes (Optional)
            </Label>
            <Textarea
              id="notes"
              placeholder="How did you feel? Any achievements?"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="min-h-[100px] resize-none border-2 border-border font-semibold"
            />
          </div>
        </Card>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          className="h-12 w-full rounded-xl text-base font-bold"
          size="lg"
        >
          Log Activity
        </Button>
      </div>
    </div>
  );
};

export default LogActivity;
